import { useState, useEffect } from 'react'
import { Link } from '@inertiajs/react'
import { GuestLayout } from '@/layouts/GuestLayout'
import { PoiIcon } from '@/lib/poi-icon'
import { trackEvent } from '@/lib/trackEngagement'
import type { GuestGuide, GuestItem, GuestProperty } from '@/lib/types'

interface Props {
  property: GuestProperty
  guide: GuestGuide
  items: GuestItem[]
  content_state: 'empty' | 'thin' | 'ready'
  share_token: string
}

type TimeFilter = 'all' | 'morning' | 'afternoon' | 'evening'

function getTimeOfDay(): TimeFilter {
  const h = new Date().getHours()
  if (h < 11) return 'morning'
  if (h < 17) return 'afternoon'
  return 'evening'
}

// Whether a POI belongs in the given time-of-day tab. Mirrors the server-side
// `GuideItem.for_time_of_day` scope: a spot shows when it has no time context,
// when it is explicitly all-day, or when its slot matches the active tab.
export function matchesTimeFilter(item: GuestItem, filter: TimeFilter): boolean {
  if (filter === 'all') return true
  if (!item.time_context) return true
  if (item.time_context === 'all_day') return true
  return item.time_context === filter
}

function hostInitials(name: string | null): string {
  if (!name) return '?'
  return name.split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2)
}


function PoiCard({ item, property, shareToken }: { item: GuestItem; property: GuestProperty; shareToken: string }) {
  const initials = hostInitials(property.host_name)
  const hasImage = !!item.image_url

  return (
    <Link
      href={`/g/${shareToken}/poi/${item.id}`}
      className="g-tap block overflow-hidden"
      style={{
        background: 'var(--g-surface, #fff)',
        borderRadius: 22,
        border: '1px solid var(--g-line)',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      {/* Image / placeholder */}
      <div
        style={{ height: 156, position: 'relative', overflow: 'hidden' }}
        className={hasImage ? undefined : 'g-poi-stripe'}
      >
        {hasImage && (
          <img
            src={item.image_url!}
            alt={item.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        <div
          style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(0,0,0,0.18) 100%)',
          }}
        />
      </div>

      {/* Card body */}
      <div style={{ padding: '16px 16px 18px' }}>
        {/* Category · distance meta */}
        {(item.category) && (
          <div
            className="g-mono"
            style={{
              fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase',
              color: 'var(--ink-500)', marginBottom: 10,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            {item.category_icon && <PoiIcon name={item.category_icon} size={12} />}
            <span>{item.category}</span>
          </div>
        )}

        {/* Name */}
        <div
          className="g-serif"
          style={{ fontSize: 26, lineHeight: 1.05, letterSpacing: '-0.01em', color: 'var(--ink-900)', marginBottom: 12 }}
        >
          {item.name}
        </div>

        {/* Host quote */}
        {item.host_note && (
          <div
            style={{
              display: 'flex', gap: 10, alignItems: 'flex-start',
              paddingTop: 12, borderTop: '1px solid var(--g-line)',
            }}
          >
            <div
              style={{
                width: 28, height: 28, borderRadius: 999,
                background: property.brand_color, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', flexShrink: 0,
              }}
            >
              {initials}
            </div>
            <p
              style={{ fontSize: 14, lineHeight: 1.4, color: 'var(--ink-700)', fontStyle: 'italic', margin: 0 }}
            >
              "{item.host_note}"
            </p>
          </div>
        )}
      </div>
    </Link>
  )
}

function EventRow({ item, property, shareToken }: { item: GuestItem; property: GuestProperty; shareToken: string }) {
  const date = item.starts_at ? new Date(item.starts_at) : null
  const dayName = date?.toLocaleDateString(undefined, { weekday: 'short' }) ?? ''
  const dayNum = date?.getDate() ?? ''
  const timeStr = date?.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) ?? ''

  return (
    <Link
      href={`/g/${shareToken}/event/${item.id}`}
      className="g-tap flex items-center gap-3"
      style={{
        background: 'var(--g-surface, #fff)',
        borderRadius: 18, padding: 16,
        border: '1px solid var(--g-line)',
        textDecoration: 'none', color: 'inherit',
      }}
    >
      {/* Date column */}
      <div style={{ width: 48, flexShrink: 0, textAlign: 'center', color: property.brand_color }}>
        <div className="g-mono" style={{ fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 2 }}>
          {dayName}
        </div>
        <div className="g-serif" style={{ fontSize: 28, lineHeight: 1 }}>{dayNum}</div>
      </div>

      {/* Event info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--ink-900)', marginBottom: 4, letterSpacing: '-0.01em' }}>
          {item.name}
        </div>
        {timeStr && (
          <div style={{ fontSize: 12, color: 'var(--ink-500)', display: 'flex', alignItems: 'center', gap: 5 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
            </svg>
            {timeStr}
          </div>
        )}
      </div>

      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink-500)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M12 5l7 7-7 7"/>
      </svg>
    </Link>
  )
}

const SUN_ICON = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="4"/>
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
  </svg>
)
const NOON_ICON = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="5"/>
  </svg>
)
const MOON_ICON = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
  </svg>
)

const TIME_CHIPS: { id: TimeFilter; label: string; icon?: React.ReactNode }[] = [
  { id: 'all',       label: 'All' },
  { id: 'morning',   label: 'Morning',   icon: SUN_ICON },
  { id: 'afternoon', label: 'Afternoon', icon: NOON_ICON },
  { id: 'evening',   label: 'Evening',   icon: MOON_ICON },
]

export default function Guide({ property, guide, items, content_state, share_token }: Props) {
  const [timeFilter, setTimeFilter] = useState<TimeFilter>(getTimeOfDay)

  // Split pois and events
  const poiItems   = items.filter(i => i.kind === 'poi')
  const eventItems = items.filter(i => i.kind === 'event')

  // Time-of-day filtering only makes sense once there are enough spots — with a
  // thin guide it would open empty at the wrong hour. Hide the chips and show all.
  const showTimeFilter = poiItems.length > 2

  const filteredPois = showTimeFilter
    ? poiItems.filter(i => matchesTimeFilter(i, timeFilter))
    : poiItems

  // Track guide open
  useEffect(() => { if (share_token) trackEvent(share_token, 'guide_open') }, [])

  const now = new Date().toLocaleString('en', { hour: 'numeric', minute: '2-digit' })
  const initials = hostInitials(property.host_name)

  return (
    <GuestLayout brandColor={property.brand_color}>
      <div style={{ paddingBottom: 100, background: 'var(--g-bg, #FBF7F1)' }}>

        {/* Host header */}
        <div style={{ paddingTop: 56, paddingLeft: 24, paddingRight: 24, paddingBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 36, height: 36, borderRadius: 11,
                background: property.brand_color, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', flexShrink: 0,
              }}
            >
              {initials}
            </div>
            <div>
              {property.host_name && (
                <div className="g-mono" style={{ fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 2 }}>
                  Hosted by {property.host_name}
                </div>
              )}
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink-900)' }}>{property.name}</div>
            </div>
          </div>
        </div>

        {/* Welcome message */}
        <div style={{ padding: '20px 24px 22px' }}>
          <p className="g-serif" style={{ fontSize: 38, lineHeight: 1.05, letterSpacing: '-0.015em', color: 'var(--ink-900)', margin: 0 }}>
            {guide.intro_text || `Welcome to ${property.name}.`}
          </p>
        </div>

        {/* Time filter — only when there are enough spots to filter */}
        {showTimeFilter && (
        <div style={{ paddingLeft: 24, marginBottom: 22 }}>
          <div className="g-mono" style={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 10 }}>
            Right now · {now}
          </div>
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, paddingRight: 24 }}>
            {TIME_CHIPS.map(chip => {
              const active = timeFilter === chip.id
              return (
                <button
                  key={chip.id}
                  onClick={() => setTimeFilter(chip.id)}
                  aria-pressed={active}
                  className="g-tap"
                  style={{
                    background: active ? 'var(--ink-900)' : 'var(--g-surface, #fff)',
                    color: active ? 'var(--sand-50, #FBF7F1)' : 'var(--ink-700)',
                    border: active ? 'none' : '1px solid var(--g-line)',
                    borderRadius: 999, padding: '8px 14px',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontSize: 13, fontWeight: 500, letterSpacing: '-0.01em',
                    cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                  }}
                >
                  {chip.icon}
                  {chip.label}
                </button>
              )
            })}
          </div>
        </div>
        )}

        {/* POI cards */}
        <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {filteredPois.map(item => (
            <PoiCard key={`poi-${item.id}`} item={item} property={property} shareToken={share_token} />
          ))}
          {showTimeFilter && filteredPois.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink-500)', fontSize: 14, fontStyle: 'italic' }}>
              Nothing in our guide for this time of day —{' '}
              <button
                onClick={() => setTimeFilter('all')}
                style={{ background: 'none', border: 'none', color: 'var(--brand)', cursor: 'pointer', textDecoration: 'underline', fontSize: 'inherit' }}
              >
                try All
              </button>
            </div>
          )}
        </div>

        {/* Events section */}
        {eventItems.length > 0 && (
          <div style={{ marginTop: 32 }}>
            <div style={{ padding: '0 24px 12px', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
              <div className="g-serif" style={{ fontSize: 24, color: 'var(--ink-900)' }}>Happening this week</div>
              <div className="g-mono" style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
                {eventItems.length} {eventItems.length === 1 ? 'event' : 'events'}
              </div>
            </div>
            <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {eventItems.map(item => (
                <EventRow key={`event-${item.id}`} item={item} property={property} shareToken={share_token} />
              ))}
            </div>
          </div>
        )}

        {content_state === 'empty' && (
          <div style={{ padding: '40px 24px 8px', textAlign: 'center', color: 'var(--ink-700)' }}>
            <p className="g-serif" style={{ fontSize: 20, lineHeight: 1.25, color: 'var(--ink-900)', margin: 0 }}>
              We&rsquo;re hand-picking the best local spots.
            </p>
            <p style={{ fontSize: 14, lineHeight: 1.45, color: 'var(--ink-500)', margin: '8px 0 20px' }}>
              A few favourites are on the way. In the meantime, here&rsquo;s everything you need for your stay.
            </p>
            <Link
              href={`/g/${share_token}/inside`}
              className="g-tap"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: property.brand_color, color: '#fff',
                borderRadius: 999, padding: '12px 20px',
                fontSize: 14, fontWeight: 600, textDecoration: 'none',
              }}
            >
              WiFi &amp; house info
            </Link>
          </div>
        )}
      </div>
    </GuestLayout>
  )
}
